import { ApolloQueryResult } from "@apollo/client";
import { AddedToCartProduct } from "src/types/addedToCardProduct.d";
import { FeatureFlags } from "src/types/featureFlags";
import { UserPreferences, UserPreferencesByEmail } from "src/types/graphql.d";
import { Money } from "src/types/ctgraphql.d";
import { StoreSettingsData } from "src/hooks/useStoreSettings";

export enum ViewedProductState {
  UNINITIALIZED = 1,
  LOADING,
  READY,
  ERROR,
}

export type ViewedPageProperties = {
  brand?: string;
  brand_id?: string;
  category_name?: string;
  category_id?: string;
  collection?: string;
  collection_id?: string;
  page_type?: string;
  isProductLoaded?: boolean;
};

export type AppStoreState = {
  adyenMerchantId: string;
  userPreferences: UserPreferences | UserPreferencesByEmail | undefined;
  addedProdToCart: AddedToCartProduct | null | undefined;
  cartMinLineItemsTotalPriceCentAmount: number | null;
  cartMinLineItemsTotalPriceDiff: Money | null;
  refetchStoreSettings:
    | (() => Promise<ApolloQueryResult<StoreSettingsData>>)
    | undefined;
  featureFlags: FeatureFlags;
  collection: { email: string };
  viewedProductState: ViewedProductState;
  displayConsentManager: boolean;
  shouldScrollToFooter: boolean;
  homePageMounted: boolean;
  geoRedirectCardOpen: boolean;
  initializedConsent: boolean;
  pageViewedProperties: ViewedPageProperties;
  consentUpdated: boolean;
  signingOut: boolean;
};

export const INITIAL_STATE: AppStoreState = {
  adyenMerchantId: "",
  userPreferences: undefined,
  addedProdToCart: undefined,
  cartMinLineItemsTotalPriceCentAmount: null,
  cartMinLineItemsTotalPriceDiff: null,
  refetchStoreSettings: undefined,
  featureFlags: {
    wishlists: false,
    storeBadges: false,
    privateSale: false,
    expressCheckout: false,
  },
  collection: { email: "" },
  viewedProductState: ViewedProductState.UNINITIALIZED,
  displayConsentManager: true,
  shouldScrollToFooter: false,
  homePageMounted: false,
  geoRedirectCardOpen: false,
  initializedConsent: false,
  pageViewedProperties: {},
  consentUpdated: false,
  signingOut: false,
};

let state: AppStoreState = INITIAL_STATE;

const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

export const appStore = {
  subscribe: (listener: () => void) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
  },
  getSnapshot: () => state,
  getServerSnapshot: () => INITIAL_STATE,

  setAdyenMerchantId: (adyenMerchantId: string) => {
    if (state.adyenMerchantId === adyenMerchantId) {
      return;
    }

    state = { ...state, adyenMerchantId };

    emit();
  },

  setUserPreferences: (
    userPreferences: UserPreferences | UserPreferencesByEmail | undefined
  ) => {
    if (state.userPreferences === userPreferences) {
      return;
    }

    state = { ...state, userPreferences };

    emit();
  },

  setAddedProdToCart: (addedProdToCart: AddedToCartProduct | null) => {
    if (state.addedProdToCart === addedProdToCart) {
      return;
    }

    state = { ...state, addedProdToCart };

    emit();
  },

  setCartMinLineItemsTotalPriceCentAmount: (
    cartMinLineItemsTotalPriceCentAmount: number | null
  ) => {
    if (
      state.cartMinLineItemsTotalPriceCentAmount ===
      cartMinLineItemsTotalPriceCentAmount
    ) {
      return;
    }

    state = { ...state, cartMinLineItemsTotalPriceCentAmount };

    emit();
  },

  setCartMinLineItemsTotalPriceDiff: (
    cartMinLineItemsTotalPriceDiff: Money | null
  ) => {
    if (
      state.cartMinLineItemsTotalPriceDiff === cartMinLineItemsTotalPriceDiff
    ) {
      return;
    }

    state = { ...state, cartMinLineItemsTotalPriceDiff };

    emit();
  },

  setRefetchStoreSettings: (
    refetchStoreSettings:
      | (() => Promise<ApolloQueryResult<StoreSettingsData>>)
      | undefined
  ) => {
    if (state.refetchStoreSettings === refetchStoreSettings) {
      return;
    }

    state = { ...state, refetchStoreSettings };

    emit();
  },

  setFeatureFlags: (featureFlags: FeatureFlags) => {
    if (state.featureFlags === featureFlags) {
      return;
    }

    state = { ...state, featureFlags };

    emit();
  },

  setCollection: (collection: { email: string }) => {
    if (state.collection === collection) {
      return;
    }

    state = { ...state, collection };

    emit();
  },

  setViewedProductState: (viewedProductState: ViewedProductState) => {
    if (state.viewedProductState === viewedProductState) {
      return;
    }

    state = { ...state, viewedProductState };

    emit();
  },

  setDisplayConsentManager: (displayConsentManager: boolean) => {
    if (state.displayConsentManager === displayConsentManager) {
      return;
    }

    state = { ...state, displayConsentManager };

    emit();
  },

  setShouldScrollToFooter: (shouldScrollToFooter: boolean) => {
    if (state.shouldScrollToFooter === shouldScrollToFooter) {
      return;
    }

    state = { ...state, shouldScrollToFooter };

    emit();
  },

  setHomepageMounted: (homePageMounted: boolean) => {
    if (state.homePageMounted === homePageMounted) {
      return;
    }

    state = { ...state, homePageMounted };

    emit();
  },

  setGeoRedirectCardOpen: (geoRedirectCardOpen: boolean) => {
    if (state.geoRedirectCardOpen === geoRedirectCardOpen) {
      return;
    }

    state = { ...state, geoRedirectCardOpen };

    emit();
  },

  setInitializedConsent: (initializedConsent: boolean) => {
    if (state.initializedConsent === initializedConsent) {
      return;
    }

    state = { ...state, initializedConsent };

    emit();
  },

  setPageViewedProperties: (pageViewedProperties: ViewedPageProperties) => {
    if (state.pageViewedProperties === pageViewedProperties) {
      return;
    }

    state = { ...state, pageViewedProperties };

    emit();
  },

  setConsentUpdated: (consentUpdated: boolean) => {
    if (state.consentUpdated === consentUpdated) {
      return;
    }

    state = { ...state, consentUpdated };

    emit();
  },

  setSigningOut: (signingOut: boolean) => {
    if (state.signingOut === signingOut) {
      return;
    }

    state = { ...state, signingOut };

    emit();
  },

  reset: () => {
    state = { ...INITIAL_STATE };

    emit();
  },
};
